/**
 * Page /suivi/:code — Suivi public anonyme d'un signalement (Phase 2A core L9).
 *
 * Pas d'auth. Sécurité : code 48 bits + rate-limit IP 10/min.
 *
 * Affiche au citoyen :
 *   - Numéro + sujet + catégorie + urgence
 *   - Statut courant (label humain)
 *   - Description + localisation (sans lat/lng précis)
 *   - Photos publiques (s'il y en a)
 *   - Si résolu : date + commentaire
 *
 * Ne révèle JAMAIS : email/téléphone créateur, identité d'un agent, lat/lng précis.
 *
 * Refonte R11 (mai 2026) :
 *   - PageShell glass cohérent avec halo tech + LogoMark Landmark
 *   - Form de saisie code en Card glass avec input gros et centré font-mono
 *   - Détails signalement en Card glass avec LogoMark catégorie + badges statut/urgence
 *   - Section Photos en grid 2/3 cols glass avec aspect-square
 *   - Card info "Que se passe-t-il maintenant ?" en bento glass tech
 *
 * Logique métier 100% préservée :
 *   - api.public.signalements.getByCode(code) avec 404 / 429 errors
 *   - 2 modes : form saisie si pas de code OU affichage data si code dans URL
 *   - onSearchSubmit → navigate(/suivi/CODE) en uppercase trimmed
 *   - Pattern HTML5 : `[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}`
 *   - Sections : Description / Localisation / Dates / Résolution / Photos
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import {
  Card, Button, Input, Badge, Alert, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  Landmark, AlertCircle, CheckCircle2, Clock, MapPin, Calendar,
  Construction, TreePine, Lightbulb, Trash2, Droplets, MoreHorizontal,
  Search, ArrowLeft,
} from 'lucide-react';
import { libelleSemainePlanifiee } from '@/lib/semaine-planifiee';

const CATEGORIE_ICONS: Record<string, typeof Construction> = {
  voirie: Construction,
  espaces_verts: TreePine,
  eclairage: Lightbulb,
  proprete: Trash2,
  assainissement: Droplets,
  autre: MoreHorizontal,
};

const URGENCE_VARIANT: Record<string, 'urgent' | 'warning' | 'success'> = {
  urgent: 'urgent',
  rapide: 'warning',
  normal: 'success',
};

const STATUT_VARIANT: Record<string, 'warning' | 'tech' | 'success' | 'neutral'> = {
  recu: 'warning',
  planifie: 'tech',
  en_cours: 'tech',
  resolu: 'success',
  archive: 'neutral',
};

const TOPBAR_BG = {
  backgroundImage:
    'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
    'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
};

interface SuiviData {
  codeSuivi: string;
  numero: string;
  sujet: string;
  description: string;
  categorie: string;
  urgence: string;
  statut: string;
  statutLabel: string;
  /** M10 — lundi de la semaine d'intervention (jamais le jour exact). */
  semainePlanifiee?: string | null;
  localisationAdresse: string | null;
  localisationQuartier: string | null;
  photos: Array<{ id: string; url: string }>;
  createdAt: string;
  resoluAt: string | null;
  resoluCommentaire: string | null;
  tenantNom: string;
}

export function SuiviSignalementPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SuiviData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form si pas de code dans l'URL
  const [searchCode, setSearchCode] = useState('');

  useEffect(() => {
    if (!code) {
      setLoading(false);
      return;
    }
    async function load(c: string) {
      setLoading(true);
      setError(null);
      try {
        const res = await api.public.signalements.getByCode(c);
        setData(res);
      } catch (err) {
        if (err instanceof ApiException) {
          if (err.status === 404) {
            setError('Code de suivi inconnu. Vérifiez l\'orthographe ou contactez votre mairie.');
          } else if (err.status === 429) {
            setError('Trop de requêtes. Patientez quelques instants avant de réessayer.');
          } else {
            setError(err.payload.error || 'Erreur');
          }
        } else {
          setError('Erreur réseau');
        }
      } finally {
        setLoading(false);
      }
    }
    void load(code);
  }, [code]);

  function onSearchSubmit(e: FormEvent) {
    e.preventDefault();
    if (searchCode.trim()) {
      navigate(`/suivi/${searchCode.trim().toUpperCase()}`);
    }
  }

  // ============================================================================
  // FORM (si pas de code dans l'URL)
  // ============================================================================

  if (!code) {
    return (
      <PageShell>
        <Card variant="glass" padded>
          <div className="text-center">
            <LogoMark icon={Search} accent="tech" size="lg" className="mx-auto" />
            <SectionEyebrow as="div" className="mt-3">Suivi public</SectionEyebrow>
            <h1 className="mt-1 font-display text-xl font-semibold tracking-r-snug text-ink">
              Suivre un signalement
            </h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-ink-muted leading-relaxed">
              Saisissez le code de suivi qui vous a été envoyé par email lors de l'enregistrement de votre signalement.
            </p>
          </div>

          <form onSubmit={onSearchSubmit} className="mx-auto mt-6 max-w-md space-y-3">
            <Input
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
              placeholder="AAAA-BBBB-CCCC"
              className="text-center font-mono text-lg tracking-wider"
              pattern="[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}"
              title="Format : 3 groupes de 4 caractères séparés par des tirets"
              required
            />
            <Button type="submit" variant="primary" size="lg" className="w-full">
              <Search className="h-4 w-4" aria-hidden="true" /> Suivre
            </Button>
          </form>
        </Card>
      </PageShell>
    );
  }

  // ============================================================================
  // LOADING
  // ============================================================================

  if (loading) {
    return (
      <PageShell>
        <Card variant="glass" padded>
          <div className="py-12 text-center">
            <div
              className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-tech border-t-transparent"
              role="status"
              aria-live="polite"
            />
            <p className="mt-4 text-sm text-ink-muted">Chargement de votre signalement…</p>
          </div>
        </Card>
      </PageShell>
    );
  }

  // ============================================================================
  // ERROR
  // ============================================================================

  if (error || !data) {
    return (
      <PageShell>
        <Card variant="glass" padded>
          <div className="py-8 text-center">
            <LogoMark icon={AlertCircle} accent="urgent" size="lg" className="mx-auto" />
            <h1 className="mt-4 font-display text-xl font-semibold tracking-r-snug text-ink">
              Signalement introuvable
            </h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-ink-muted leading-relaxed">{error}</p>
            <div className="mt-6 flex flex-col items-center gap-2">
              <Link to="/suivi">
                <Button variant="secondary" size="sm">
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Saisir un autre code
                </Button>
              </Link>
              <Link
                to="/"
                className="rounded-r-md px-1 text-sm text-ink-muted hover:text-tech-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                Retour à l'accueil
              </Link>
            </div>
          </div>
        </Card>
      </PageShell>
    );
  }

  // ============================================================================
  // DATA OK
  // ============================================================================

  const CatIcon = CATEGORIE_ICONS[data.categorie] ?? MoreHorizontal;
  const dateCreated = new Date(data.createdAt).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const dateResolved = data.resoluAt
    ? new Date(data.resoluAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <PageShell tenantNom={data.tenantNom}>
      <Card variant="glass" padded>
        <div className="flex items-start gap-3">
          <LogoMark icon={CatIcon} accent="tech" size="md" />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={STATUT_VARIANT[data.statut] ?? 'neutral'} size="sm">
                {data.statutLabel}
              </Badge>
              {libelleSemainePlanifiee(data.semainePlanifiee) && (
                <span className="text-xs font-medium text-tech">
                  {libelleSemainePlanifiee(data.semainePlanifiee)}
                </span>
              )}
              <Badge variant={URGENCE_VARIANT[data.urgence] ?? 'success'} size="sm">
                Urgence {data.urgence}
              </Badge>
            </div>
            <h1 className="mt-2 font-display text-xl font-semibold tracking-r-snug text-ink">
              {data.sujet}
            </h1>
            <p className="mt-1 text-xs text-ink-muted">
              Référence <span className="font-mono text-ink">{data.numero}</span> · Code <span className="font-mono text-tech-fg">{data.codeSuivi}</span>
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-5">
          <Section title="Description">
            <p className="whitespace-pre-wrap text-sm text-ink leading-relaxed">{data.description}</p>
          </Section>

          {(data.localisationAdresse || data.localisationQuartier) && (
            <Section title="Localisation">
              <div className="flex items-start gap-2 rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass p-3 text-sm text-ink">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-tech-fg" aria-hidden="true" />
                <div>
                  {data.localisationAdresse && <div>{data.localisationAdresse}</div>}
                  {data.localisationQuartier && (
                    <div className="text-ink-muted">Quartier {data.localisationQuartier}</div>
                  )}
                </div>
              </div>
            </Section>
          )}

          <Section title="Dates">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-ink-muted" aria-hidden="true" />
                <span className="text-ink-muted">Déposé le</span>
                <span className="text-ink font-medium">{dateCreated}</span>
              </div>
              {dateResolved && (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
                  <span className="text-ink-muted">Résolu le</span>
                  <span className="text-ink font-medium">{dateResolved}</span>
                </div>
              )}
            </div>
          </Section>

          {data.resoluCommentaire && (
            <Section title="Commentaire de résolution">
              <Alert variant="success" icon={CheckCircle2}>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{data.resoluCommentaire}</p>
              </Alert>
            </Section>
          )}

          {data.photos.length > 0 && (
            <Section title={`Photos (${data.photos.length})`}>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {data.photos.map((p) => (
                  <a
                    key={p.id}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block aspect-square overflow-hidden rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass transition-all hover:border-tech/40 hover:shadow-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                  >
                    <img src={p.url} alt="Photo signalement" className="h-full w-full object-cover" />
                  </a>
                ))}
              </div>
            </Section>
          )}
        </div>
      </Card>

      <Card variant="glass" padded className="mt-3 border-tech/30">
        <div className="flex items-start gap-3">
          <div
            className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-tech/15 text-tech-fg flex-shrink-0"
            aria-hidden="true"
          >
            <Clock className="h-4 w-4" />
          </div>
          <div>
            <h3 className="font-display text-sm font-semibold tracking-r-snug text-ink">
              Que se passe-t-il maintenant ?
            </h3>
            <ul className="mt-2 space-y-1 text-xs text-ink-muted leading-relaxed">
              <li>• Les services techniques de <strong className="text-ink">{data.tenantNom}</strong> ont reçu votre signalement</li>
              <li>• Il sera examiné par un agent dans les meilleurs délais</li>
              <li>• Vous serez notifié(e) par email à chaque changement d'étape importante</li>
              <li>• Vous pouvez revenir sur cette page à tout moment avec votre code de suivi</li>
            </ul>
          </div>
        </div>
      </Card>

      <div className="mt-4 text-center">
        <Link
          to="/"
          className="rounded-r-md px-1 text-sm text-ink-muted transition-colors hover:text-tech-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          Retour à l'accueil
        </Link>
      </div>
    </PageShell>
  );
}

function PageShell({ children, tenantNom }: { children: React.ReactNode; tenantNom?: string }) {
  return (
    <div className="min-h-screen text-ink">
      <header
        className="sticky top-0 z-20 border-b border-border backdrop-blur-glass px-4 py-3"
        style={TOPBAR_BG}
      >
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <LogoMark icon={Landmark} accent="tech" size="md" aria-label="Mairie" />
          <div className="min-w-0 flex-1">
            <SectionEyebrow>{tenantNom ? 'Mairie de' : 'Service public'}</SectionEyebrow>
            <div className="font-display text-sm font-semibold tracking-r-snug text-ink truncate">
              {tenantNom ?? 'Suivi signalement'}
              {tenantNom && <span className="ml-1 text-ink-muted font-normal">· Suivi signalement</span>}
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>
      <main id="main-content" className="mx-auto max-w-2xl px-4 py-6 sm:px-6 animate-fade-in">
        {children}
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <SectionEyebrow as="h3">{title}</SectionEyebrow>
      <div className="mt-2">{children}</div>
    </div>
  );
}
